import { API_PREFIX } from './endpoints/api.constants';

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

export const tokenStorage = {
  getAccessToken: (): string | null => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefreshToken: (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY),
  
  setTokens: (accessToken: string, refreshToken?: string): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    syncMediaSession(accessToken);
    
    if (refreshToken && refreshToken !== "") {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  },
  
  clearTokens: (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

/** The tenant an access token was issued for (its `tenant_id` claim), or null. */
export function tokenTenantId(token: string | null | undefined): number | null {
  try {
    const part = (token || '').split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const id = Number(JSON.parse(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '='))).tenant_id);
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

/**
 * After a sign-in, remember the tenant the server signed the user into (UAT DEF-233). The login request names the
 * tenant this browser remembered from an earlier session; an account of another tenant is signed in to its own,
 * and every later request has to name that one.
 */
export function adoptSignInTenant(accessToken: string | null | undefined): void {
  const id = tokenTenantId(accessToken);
  if (id) localStorage.setItem('tenant_id', String(id));
}
/**
 * Plain <video>, <audio>, <track>, <iframe> and links can't send the
 * Authorization header, so protected files (videos, PDFs, subtitles…) loaded
 * by them got 401. The API hands this session an HttpOnly, SameSite=Lax media
 * cookie that can only read uploaded files (useradmin: POST
 * /uploads/media-session). Called whenever a new access token is stored, and
 * once at startup for an existing session.
 */
export function syncMediaSession(token: string | null = localStorage.getItem(ACCESS_TOKEN_KEY)): Promise<void> {
  if (!token) return Promise.resolve();
  return fetch(`${API_PREFIX}/uploads/media-session`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  }).then(() => undefined, () => undefined);
}
